/* ===================== MY ARTICLES (ASTROLOGER) =====================
   An astrologer writing for the public blog: their own articles, what state
   each one is in, and the one action each state allows.

   The flow, which this screen exists to make obvious:

     draft ──submit──▶ waiting for review ──admin approves──▶ published
       ▲                      │
       └──── edit ────────────┴──admin rejects──▶ needs changes

   Nothing here publishes. The API accepts no status from an author at all,
   so "Submit for review" is the furthest this screen can move an article —
   an admin decides the rest, and the author is notified either way.

   `can_edit` / `can_submit` come from the server on every row; the buttons
   read those rather than re-deriving the state machine in the browser, so a
   rule changing server-side does not leave a dead button here.

   GET /my-blog-posts answers 404 when the account has no astrologer profile
   and 403 when that profile is not approved or not active — the API's own
   convention for every astrologer endpoint, handled as two plain
   explanations below rather than an error toast.
   ==================================================================== */
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@astro/api'
import { useAuth } from '@astro/auth'
import { appUrl } from '@astro/config'
import { LuExternalLink, LuNotebookPen, LuPencil, LuPlus, LuSend, LuTrash2 } from 'react-icons/lu'
import BlogComposerModal from './BlogComposerModal'
import { statusMeta, STATUS_ORDER, dateLabel } from './statusMeta'
import './preview.css'

const EMPTY_COUNTS = { draft: 0, pending_review: 0, published: 0, rejected: 0 }

function StatusBadge({ status }) {
  const meta = statusMeta(status)
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-bold ${meta.badge}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
      {meta.label}
    </span>
  )
}

function Notice({ title, children }) {
  return (
    <div className="bg-white p-6 border border-stone-100 shadow-sm rounded-2xl">
      <h2 className="text-lg font-bold text-stone-900 font-royal">{title}</h2>
      <p className="text-sm text-stone-500 mt-2">{children}</p>
    </div>
  )
}

export default function BlogAuthoringTab() {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [composing, setComposing] = useState(null) // null | 'new' | post
  const [error, setError] = useState(null)

  const query = useQuery({
    queryKey: ['dashboard', 'my-blog-posts'],
    queryFn: () => api.blog.mine.list({ per_page: 50 }),
    enabled: Boolean(user),
    retry: false,
  })

  const posts = Array.isArray(query.data?.data) ? query.data.data : []
  const counts = { ...EMPTY_COUNTS, ...query.data?.meta?.counts }

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['dashboard', 'my-blog-posts'] })

  const submit = useMutation({
    mutationFn: (id) => api.blog.mine.submit(id),
    onSuccess: invalidate,
    onError: (err) => setError(err?.message || 'Could not submit the article. Please try again.'),
  })

  const remove = useMutation({
    mutationFn: (id) => api.blog.mine.remove(id),
    onSuccess: invalidate,
    onError: (err) => setError(err?.message || 'Could not delete the article.'),
  })

  // The API's two "you are not an astrologer (yet)" answers. Both are states of
  // the account, not failures of this screen, so they read as sentences.
  if (query.isError && query.error?.status === 404) {
    return (
      <Notice title="Writing for the blog">
        Articles are written by astrologers. We could not find an astrologer profile on this account.
      </Notice>
    )
  }
  if (query.isError && query.error?.status === 403) {
    return <Notice title="Writing for the blog">{query.error.message}</Notice>
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      {/* ── Header + the tally that tells the author what needs them ── */}
      <div className="bg-white p-4 sm:p-6 border border-stone-100 shadow-sm rounded-2xl">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <h2 className="text-lg sm:text-xl font-bold text-stone-900 font-royal">My articles</h2>
            <p className="text-sm text-stone-500 mt-1">
              Write for the public blog under your own byline. Every article is read by an admin before it is published.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setComposing('new')}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#C8922A] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#b07f22] shrink-0"
          >
            <LuPlus className="w-4 h-4" /> Write an article
          </button>
        </div>

        <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
          {STATUS_ORDER.map((status) => {
            const meta = statusMeta(status)
            return (
              <div key={status} className="rounded-xl border border-stone-100 bg-stone-50/60 px-3.5 py-3">
                <p className="text-xl font-bold text-stone-900">{counts[status] ?? 0}</p>
                <p className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-stone-500 mt-0.5">
                  <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
                  {meta.short}
                </p>
              </div>
            )
          })}
        </div>
      </div>

      {/* ── The articles ── */}
      <div className="bg-white p-4 sm:p-6 border border-stone-100 shadow-sm rounded-2xl">
        {query.isPending ? (
          <div className="space-y-2 animate-pulse" aria-busy="true">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-16 bg-stone-50 rounded-xl" />
            ))}
          </div>
        ) : posts.length === 0 ? (
          <div className="py-10 text-center">
            <LuNotebookPen className="w-8 h-8 mx-auto text-stone-300" />
            <p className="text-sm font-semibold text-stone-700 mt-3">You have not written an article yet.</p>
            <p className="text-xs text-stone-400 mt-1 max-w-sm mx-auto">
              Share what you know — a transit, a remedy, a reading you keep explaining. An admin reviews it before it
              goes live.
            </p>
            <button
              type="button"
              onClick={() => setComposing('new')}
              className="inline-flex items-center gap-2 rounded-xl bg-[#C8922A] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#b07f22] mt-5"
            >
              <LuPlus className="w-4 h-4" /> Write your first article
            </button>
          </div>
        ) : (
          <ul className="divide-y divide-stone-100">
            {posts.map((post) => {
              const meta = statusMeta(post.status)
              return (
                <li key={post.id} className="py-4 first:pt-0 last:pb-0">
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2.5">
                        <StatusBadge status={post.status} />
                        {post.category?.name && (
                          <span className="text-[10px] font-bold uppercase tracking-wider text-amber-700/70">
                            {post.category.name}
                          </span>
                        )}
                      </div>
                      <h3 className="text-[15px] font-bold text-stone-900 mt-1.5 truncate">{post.title}</h3>
                      <p className="text-xs text-stone-400 mt-0.5">
                        {post.status === 'published'
                          ? `Published ${dateLabel(post.published_at)}`
                          : post.status === 'pending_review'
                            ? `Submitted ${dateLabel(post.submitted_at)}`
                            : `Last saved ${dateLabel(post.updated_at)}`}
                        {post.status === 'published' && typeof post.views === 'number' && ` · ${post.views} reads`}
                      </p>
                      <p className="text-[11px] text-stone-400 mt-1">{meta.note}</p>

                      {post.status === 'rejected' && post.rejection_reason && (
                        <div className="mt-2 rounded-xl bg-amber-50 border border-amber-100 px-3 py-2">
                          <p className="text-[10px] font-bold uppercase tracking-wider text-amber-700">
                            What the admin asked for
                          </p>
                          <p className="text-xs text-amber-900 mt-0.5 leading-relaxed">{post.rejection_reason}</p>
                        </div>
                      )}
                    </div>

                    <div className="flex flex-wrap items-center gap-2 shrink-0">
                      {post.status === 'published' && (
                        <a
                          href={appUrl('website', `/blog/${post.slug}`)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 rounded-xl border border-stone-200 px-3 py-2 text-xs font-semibold text-stone-700 hover:bg-stone-50"
                        >
                          <LuExternalLink className="w-3.5 h-3.5" /> View
                        </a>
                      )}
                      {post.can_submit && (
                        <button
                          type="button"
                          onClick={() => submit.mutate(post.id)}
                          disabled={submit.isPending}
                          className="inline-flex items-center gap-1.5 rounded-xl bg-[#C8922A] px-3 py-2 text-xs font-bold text-white hover:bg-[#b07f22] disabled:opacity-50"
                        >
                          <LuSend className="w-3.5 h-3.5" /> Submit for review
                        </button>
                      )}
                      {post.can_edit && (
                        <button
                          type="button"
                          onClick={() => setComposing(post)}
                          className="inline-flex items-center gap-1.5 rounded-xl border border-stone-200 px-3 py-2 text-xs font-semibold text-stone-700 hover:bg-stone-50"
                        >
                          <LuPencil className="w-3.5 h-3.5" /> Edit
                        </button>
                      )}
                      {post.can_edit && (
                        <button
                          type="button"
                          title="Delete"
                          aria-label={`Delete ${post.title}`}
                          onClick={() => remove.mutate(post.id)}
                          disabled={remove.isPending}
                          className="rounded-xl border border-transparent p-2 text-stone-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-50"
                        >
                          <LuTrash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </div>

      {composing && (
        <BlogComposerModal post={composing === 'new' ? null : composing} onClose={() => setComposing(null)} />
      )}
    </div>
  )
}
